import React, { useState, useEffect, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import BusinessIcon from "@material-ui/icons/Business";
import GroupWorkIcon from "@material-ui/icons/GroupWork";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

import { getMyTeams } from "../../services/access.api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: -8,
    marginBottom: 16
  },
  contextRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  teamBlock: {
    marginBottom: theme.spacing(4)
  },
  teamHeader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  teamName: {
    fontSize: 18,
    fontWeight: 600
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: "center"
  },
  activityBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor:
      theme.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"
  },
  dot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 6
  },
  meRow: {
    fontWeight: 600
  }
}));

/**
 * «Mi equipo» — el contexto de pertenencia del usuario (doc 0380 H4).
 *
 * WHY existe: el rol SUPERVISOR cambiaba lo que alguien VEÍA pero no le daba
 * nada que MIRAR, y un USER no sabía a qué empresa pertenecía, quién era su
 * supervisor ni con quién compartía departamento. El endpoint `/me/teams` ya
 * existía y no tenía ni un consumidor.
 *
 * Es de solo lectura a propósito: quien administra equipos lo hace en
 * Subagrupaciones, con sus permisos. Aquí solo se consulta.
 */
const MyTeam = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    let active = true;

    const fetchTeams = async () => {
      try {
        const { data } = await getMyTeams();
        if (!active) return;
        setCompany(data?.company ?? null);
        setTeams(data?.teams ?? []);
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchTeams();
    return () => {
      active = false;
    };
  }, []);

  const renderTeam = (team) => (
    <div key={team.userGroupId} className={classes.teamBlock}>
      <div className={classes.teamHeader}>
        <GroupWorkIcon color="action" />
        <span className={classes.teamName}>{team.userGroupName}</span>
        {team.isSupervisor && (
          <Chip
            size="small"
            color="primary"
            icon={<SupervisorAccountIcon />}
            label={i18n.t("myTeam.youSupervise")}
          />
        )}
        {!team.isSupervisor && team.roleName && (
          <Chip size="small" variant="outlined" label={team.roleName} />
        )}
      </div>

      {team.description && (
        <Typography className={classes.hint}>{team.description}</Typography>
      )}

      <Typography className={classes.sectionLabel}>
        {i18n.t("myTeam.supervisor")}
      </Typography>
      <Typography>
        {team.supervisor
          ? team.supervisor.userName
          : i18n.t("myTeam.noSupervisor")}
      </Typography>

      <Typography className={classes.sectionLabel}>
        {i18n.t("myTeam.departments")}
      </Typography>
      {team.queues?.length > 0 ? (
        <div className={classes.contextRow}>
          {team.queues.map((q) => (
            <Chip
              key={q.queueId}
              size="small"
              label={q.queueName}
              style={q.queueColor ? { backgroundColor: q.queueColor, color: "#fff" } : undefined}
            />
          ))}
        </div>
      ) : (
        <Typography className={classes.hint}>
          {i18n.t("myTeam.noDepartments")}
        </Typography>
      )}

      <Typography className={classes.sectionLabel}>
        {i18n.t("myTeam.members")}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("myTeam.table.name")}</TableCell>
            <TableCell>{i18n.t("myTeam.table.role")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {team.members?.map((m) => (
            <TableRow key={m.userId}>
              <TableCell className={m.userId === user?.id ? classes.meRow : undefined}>
                {m.userName}
                {m.userId === user?.id && ` (${i18n.t("myTeam.you")})`}
              </TableCell>
              <TableCell>
                {m.isSupervisor ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={i18n.t("myTeam.supervisor")}
                  />
                ) : (
                  m.roleName || i18n.t("myTeam.inheritsRole")
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/*
        Panel de supervisión: solo llega del backend cuando el solicitante es el
        supervisor de ESTE equipo. Ver la carga de trabajo ajena es una capacidad
        de supervisión, no de pertenencia (doc 0380 H5).

        Da contenido real a la promesa que la pantalla de Subagrupaciones ya
        hacía: «el supervisor de un equipo ve la actividad de sus miembros».
      */}
      {team.isSupervisor && team.activity?.length > 0 && (
        <>
          <Typography className={classes.sectionLabel}>
            {i18n.t("myTeam.activity")}
          </Typography>
          <div className={classes.activityBox}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("myTeam.table.name")}</TableCell>
                  <TableCell align="center">
                    {i18n.t("myTeam.table.state")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("myTeam.table.handling")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("myTeam.table.closedToday")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {team.activity.map((a) => (
                  <TableRow key={a.userId}>
                    <TableCell
                      className={a.userId === user?.id ? classes.meRow : undefined}
                    >
                      {a.userName}
                      {a.isSupervisor && ` (${i18n.t("myTeam.supervisor").toLowerCase()})`}
                    </TableCell>
                    <TableCell align="center">
                      <span
                        className={classes.dot}
                        style={{ backgroundColor: a.online ? "#4caf50" : "#bdbdbd" }}
                      />
                      {a.online
                        ? i18n.t("myTeam.online")
                        : i18n.t("myTeam.offline")}
                    </TableCell>
                    <TableCell align="center">{a.atendiendo}</TableCell>
                    <TableCell align="center">{a.cerradosHoy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <Divider style={{ marginTop: 24 }} />
    </div>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("myTeam.title")}</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Typography className={classes.subtitle}>
          {i18n.t("myTeam.subtitle")}
        </Typography>

        <div className={classes.contextRow}>
          <BusinessIcon color="action" />
          <Typography>
            <strong>{company?.name || "—"}</strong>
          </Typography>
        </div>

        <Divider style={{ marginBottom: 24 }} />

        {loading ? (
          <Table size="small">
            <TableBody>
              <TableRowSkeleton columns={2} />
            </TableBody>
          </Table>
        ) : teams.length === 0 ? (
          <div className={classes.empty}>{i18n.t("myTeam.noTeams")}</div>
        ) : (
          teams.map(renderTeam)
        )}
      </Paper>
    </MainContainer>
  );
};

export default MyTeam;
